import { useEffect, useRef, useState } from "react";
import { MARKETS } from "../../shared/domain.ts";
import { STORAGE_KEY, type Settings as SettingsValues } from "../game/storage.ts";

/**
 * Settings, kept to the things the game genuinely cannot decide for the player.
 *
 * Volume, because this is an audio game played in public places. Motion, because
 * the Reveal got theatrical and not everyone wants that. And an erase, because
 * every Streak and every result lives in this browser's localStorage and nowhere
 * else — a game that stores your history locally owes you a way to delete it.
 *
 * A native `<dialog>` rather than a hand-rolled overlay: Escape, focus trapping
 * and inertness of the page behind come for free and are easy to get wrong.
 */

export interface SettingsProps {
  values: SettingsValues;
  onChange: (next: SettingsValues) => void;
  /** Catalog facts, shown so "why isn't song X in here?" has an answer. */
  info: { trackCount: number | null; builtAt: string | null };
}

export function SettingsButton({ values, onChange, info }: SettingsProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        className="iconbtn"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Settings"
        title="Settings"
      >
        <GearIcon />
      </button>
      {open && <SettingsDialog values={values} onChange={onChange} info={info} onClose={() => setOpen(false)} />}
    </>
  );
}

function SettingsDialog({
  values,
  onChange,
  info,
  onClose,
}: SettingsProps & { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    // jsdom-free environments and very old browsers lack showModal; the dialog
    // still renders, just without the backdrop.
    dialog?.showModal?.();
  }, []);

  function close() {
    ref.current?.close?.();
    onClose();
  }

  function reset() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Private mode or a blocked store — the reload still lands on a fresh state.
    }
    window.location.reload();
  }

  return (
    <dialog className="sheet" ref={ref} onCancel={close} onClose={onClose} aria-label="Settings">
      <div className="sheet__head">
        <h2 className="sheet__title">Settings</h2>
        <button className="iconbtn" type="button" onClick={close} aria-label="Close settings">
          ✕
        </button>
      </div>

      <div className="sheet__body">
        <label className="setting">
          <span className="setting__label">
            Volume
            <span className="setting__value">{Math.round(values.volume * 100)}%</span>
          </span>
          <input
            className="setting__range"
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(values.volume * 100)}
            onChange={(e) => onChange({ ...values, volume: Number(e.target.value) / 100 })}
          />
        </label>

        <label className="setting setting--row">
          <span className="setting__label">
            Reduce motion
            <span className="setting__hint">Turns off the reveal animation and confetti.</span>
          </span>
          <input
            className="setting__check"
            type="checkbox"
            checked={values.reduceMotion}
            onChange={(e) => onChange({ ...values, reduceMotion: e.target.checked })}
          />
        </label>

        <div className="setting">
          <span className="setting__label">
            Songs
            <span className="setting__hint">
              {info.trackCount === null
                ? "Loading the song list…"
                : `${info.trackCount.toLocaleString()} songs, licensed for ${MARKETS.join(", ")}.`}
              {info.builtAt && ` Updated ${new Date(info.builtAt).toLocaleDateString()}.`}
            </span>
          </span>
        </div>

        <div className="setting setting--danger">
          <span className="setting__label">
            Reset everything
            <span className="setting__hint">
              Deletes your streaks, results and history from this browser. Nothing is stored
              anywhere else, so this cannot be undone.
            </span>
          </span>
          {confirmingReset ? (
            <div className="setting__confirm">
              <button className="btn btn--ghost" type="button" onClick={() => setConfirmingReset(false)}>
                Cancel
              </button>
              <button className="btn btn--danger" type="button" onClick={reset}>
                Delete it all
              </button>
            </div>
          ) : (
            <button className="btn btn--ghost" type="button" onClick={() => setConfirmingReset(true)}>
              Reset…
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5Zm0-5A1.5 1.5 0 1 0 13.5 12 1.5 1.5 0 0 0 12 10.5Z"
      />
      <path
        fill="currentColor"
        d="m19.4 13.3.1-1.3-.1-1.3 1.7-1.3-1.6-2.8-2 .7a7.6 7.6 0 0 0-2.2-1.3L14.9 4h-3.2l-.4 2a7.6 7.6 0 0 0-2.2 1.3l-2-.7-1.6 2.8 1.7 1.3-.1 1.3.1 1.3-1.7 1.3 1.6 2.8 2-.7a7.6 7.6 0 0 0 2.2 1.3l.4 2h3.2l.4-2a7.6 7.6 0 0 0 2.2-1.3l2 .7 1.6-2.8Zm-2.1 2.5-.6.5a5.6 5.6 0 0 1-1.7 1l-.7.3-.2 1.4h-1.2l-.2-1.4-.7-.3a5.6 5.6 0 0 1-1.7-1l-.6-.5-1.3.5-.6-1 1.1-.9v-.7a5.4 5.4 0 0 1 0-1.4v-.7l-1.1-.9.6-1 1.3.5.6-.5a5.6 5.6 0 0 1 1.7-1l.7-.3.2-1.4h1.2l.2 1.4.7.3a5.6 5.6 0 0 1 1.7 1l.6.5 1.3-.5.6 1-1.1.9v.7a5.4 5.4 0 0 1 0 1.4v.7l1.1.9-.6 1Z"
      />
    </svg>
  );
}
